import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Search,
  Filter,
  Package,
  User,
  Calendar,
  CheckCircle,
  Clock,
  ArrowRight,
  AlertCircle,
  ChevronRight,
  Download,
  Printer
} from 'lucide-react';
import { API_URL } from '../../Config';

export default function ValidatedDemandes() {
  const navigate = useNavigate();
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('toutes');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadDemandes();
  }, [currentPage, filterStatus]);

  const loadDemandes = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/magasin/validated-demandes`, {
        headers,
        params: { page: currentPage }
      });
      
      if (res.data.success) {
        setDemandes(res.data.data.data);
        setTotalPages(res.data.data.last_page);
      }
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredDemandes = demandes.filter(demande => {
    // Filtre par recherche
    const searchMatch = 
      (demande.numero || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (demande.demandeur?.nom || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (demande.notes || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filtre par statut
    let statusMatch = true;
    if (filterStatus !== 'toutes') {
      statusMatch = demande.status === filterStatus;
    }
    
    return searchMatch && statusMatch;
  });

  const getStatusBadge = (status) => {
    const statusMap = {
      'validee': { label: 'Validée', color: 'bg-green-100 text-green-800 border-green-200' },
      'en_preparation': { label: 'En préparation', color: 'bg-orange-100 text-orange-800 border-orange-200' },
      'pret_recuperation': { label: 'Prête à récupérer', color: 'bg-blue-100 text-blue-800 border-blue-200' },
      'recuperee': { label: 'Récupérée', color: 'bg-purple-100 text-purple-800 border-purple-200' },
      'annulee': { label: 'Annulée', color: 'bg-red-100 text-red-800 border-red-200' }
    };
    
    return statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-800 border-gray-200' };
  };

  const handlePrepare = (demandeId) => {
    navigate(`/magasin/demandes/${demandeId}/preparer`);
  };

  const exportToExcel = () => {
    alert('Export Excel - Fonctionnalité à implémenter');
  };

  if (loading && demandes.length === 0) {
    return (
      <div className="min-h-screen  p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-600 border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des demandes...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen  p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                Demandes validées à préparer
              </h1>
              <p className="text-gray-600">
                Préparez les matériels pour les demandes validées
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/magasin/ready-demandes')}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center gap-2"
              >
                <Package className="w-4 h-4" />
                Demandes prêtes ({demandes.filter(d => d.status === 'pret_recuperation').length})
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="text-sm text-gray-500">Total validées</div>
              <div className="text-2xl font-bold text-gray-900">{demandes.length}</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="text-sm text-gray-500">En préparation</div>
              <div className="text-2xl font-bold text-orange-600">
                {demandes.filter(d => d.status === 'en_preparation').length}
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="text-sm text-gray-500">Prêtes</div>
              <div className="text-2xl font-bold text-blue-600">
                {demandes.filter(d => d.status === 'pret_recuperation').length}
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="text-sm text-gray-500">Récupérées</div>
              <div className="text-2xl font-bold text-green-600">
                {demandes.filter(d => d.status === 'recuperee').length}
              </div>
            </div>
          </div>

          {/* Filtres et recherche */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Rechercher par n° demande, demandeur..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>
              
              <div className="flex gap-3">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
                >
                  <option value="toutes">Tous les statuts</option>
                  <option value="validee">Validées</option>
                  <option value="en_preparation">En préparation</option>
                  <option value="pret_recuperation">Prêtes</option>
                  <option value="recuperee">Récupérées</option>
                </select>
                
                <button
                  onClick={exportToExcel}
                  className="px-4 py-2 border border-gray-300 text-gray-700 hover: rounded-lg font-medium flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Exporter
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Liste des demandes */}
        {filteredDemandes.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucune demande trouvée
            </h3>
            <p className="text-gray-600">
              {searchTerm || filterStatus !== 'toutes' 
                ? 'Aucune demande ne correspond à vos critères de recherche.'
                : 'Toutes les demandes ont été traitées. 🎉'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredDemandes.map((demande) => {
              const statusBadge = getStatusBadge(demande.status);
              const totalArticles = demande.demande_materiels?.length || 0;
              const articlesValides = demande.demande_materiels?.filter(dm => dm.statut === 'valide').length || 0;
              
              return (
                <div 
                  key={demande.id}
                  className="bg-white rounded-xl border border-gray-200 hover:border-orange-300 hover:shadow-md transition-all duration-200 overflow-hidden"
                >
                  <div className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                      {/* Informations gauche */}
                      <div className="flex-1">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-orange-50 border border-orange-200 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Package className="w-6 h-6 text-orange-600" />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                              <div>
                                <h3 className="font-semibold text-gray-900 text-lg mb-1">
                                  Demande #{demande.numero || demande.id}
                                </h3>
                                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                                  <span className="flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    {demande.demandeur?.nom || 'N/A'}
                                  </span>
                                  <span>•</span>
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {new Date(demande.date_validation).toLocaleDateString('fr-FR')}
                                  </span>
                                  <span>•</span>
                                  <span>
                                    {totalArticles} article(s) • {articlesValides} validé(s)
                                  </span>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-3">
                                <span className={`px-3 py-1 text-xs font-medium rounded-full border ${statusBadge.color}`}>
                                  {statusBadge.label}
                                </span>
                              </div>
                            </div>
                            
                            {demande.notes && (
                              <div className="mt-3 p-3  border border-gray-200 rounded-lg">
                                <p className="text-sm text-gray-600 line-clamp-2">{demande.notes}</p>
                              </div>
                            )}
                            
                            {/* Indicateur d'urgence */}
                            {demande.urgence === 'haute' && (
                              <div className="mt-3 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-1.5 rounded-lg">
                                <AlertCircle className="w-4 h-4" />
                                Demande urgente
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Actions droite */}
                      <div className="lg:w-64">
                        <div className="flex flex-col gap-3">
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className=" p-2 rounded border border-gray-200">
                              <div className="text-gray-500">Validée le</div>
                              <div className="font-medium text-gray-900">
                                {new Date(demande.date_validation).toLocaleTimeString('fr-FR', { 
                                  hour: '2-digit', 
                                  minute: '2-digit' 
                                })}
                              </div>
                            </div>
                            <div className=" p-2 rounded border border-gray-200">
                              <div className="text-gray-500">Priorité</div>
                              <div className={`font-medium ${
                                demande.urgence === 'haute' ? 'text-red-600' : 
                                demande.urgence === 'moyenne' ? 'text-yellow-600' : 'text-green-600'
                              }`}>
                                {demande.urgence || 'Normale'}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex gap-3">
                            <button
                              onClick={() => navigate(`/magasin/demandes/${demande.id}`)}
                              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 hover: rounded-lg font-medium flex items-center justify-center gap-2"
                            >
                              <ChevronRight className="w-4 h-4" />
                              Détails
                            </button>
                            
                            {demande.status === 'validee' && (
                              <button
                                onClick={() => handlePrepare(demande.id)}
                                className="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium flex items-center justify-center gap-2"
                              >
                                <Package className="w-4 h-4" />
                                Préparer
                              </button>
                            )}
                            
                            {demande.status === 'en_preparation' && (
                              <button
                                onClick={() => handlePrepare(demande.id)}
                                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center justify-center gap-2"
                              >
                                <Clock className="w-4 h-4" />
                                Continuer
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Page {currentPage} sur {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 border border-gray-300 text-gray-700 hover: rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Précédent
                    </button>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 border border-gray-300 text-gray-700 hover: rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}